// src/components/ProgramRecorder.js
// Self-contained overlay to record a PROGRAM video in-app, reusing the
// pre-recorded broadcaster. See docs/decisions/adr-program-recorded-videos.md
// (Phase 2). DELIBERATELY isolated: it owns its own program-scoped upload
// orchestration and touches NOTHING in the class-recording flow (Workouts.js).
// It only *reuses* the LiveBroadcaster component (in its existing pre-recorded
// sub-mode) and the recordingBuffer IDB service.
//
// Flow:
//   setup     — admin types a title → "Continue"
//   recording — create the programVideos doc (status 'processing') → mount
//               LiveBroadcaster keyed by that doc id; admin records + clicks
//               End Class → we set endingAt → broadcaster assembles the Blob
//               and calls onRecordingComplete
//   uploading — POST /api/mux-direct-upload { programId, programVideoId } → XHR
//               PUT the Blob to the signed URL (with progress)
//   processing— subscribe to the programVideos doc; the Mux webhook flips it to
//               'ready' → clear the IDB buffer → onSaved → close
//   error     — retry keeps the Blob (no re-record needed)
//
// Recovery tagging: while a program recording's chunks are in IDB, we set a
// localStorage marker (mom-program-recording-<docId>) so the Workouts class
// recovery scan skips it (it would otherwise surface it as an "Unknown class").

import React, { useState, useRef, useEffect, useCallback } from 'react';
import LiveBroadcaster from './LiveBroadcaster';
import { createProgramVideo, updateProgramVideo, deleteProgramVideo } from '../services/programVideos';
import { clearRecording as bufferClearRecording } from '../services/recordingBuffer';
import { db } from '../services/firebase';
import { doc, onSnapshot } from 'firebase/firestore';

const MARKER_PREFIX = 'mom-program-recording-';
export function programRecordingMarkerKey(id) { return `${MARKER_PREFIX}${id}`; }
function setMarker(id) { try { localStorage.setItem(programRecordingMarkerKey(id), '1'); } catch (_) { /* ignore */ } }
function clearMarker(id) { try { localStorage.removeItem(programRecordingMarkerKey(id)); } catch (_) { /* ignore */ } }

function ProgramRecorder({ programId, programTitle, nextOrder = 0, getToken, onClose, onSaved }) {
    const [phase, setPhase] = useState('setup'); // setup | recording | uploading | processing | error | done
    const [title, setTitle] = useState('');
    const [recordingKey, setRecordingKey] = useState(null); // programVideos doc id
    const [endingAt, setEndingAt] = useState(null);
    const [progress, setProgress] = useState(0);
    const [errorMsg, setErrorMsg] = useState('');
    const blobRef = useRef(null);       // kept for retry
    const mimeRef = useRef('video/webm');
    const xhrRef = useRef(null);
    const unsubRef = useRef(null);

    // Clean up the doc subscription on unmount.
    useEffect(() => () => { if (unsubRef.current) { try { unsubRef.current(); } catch (_) { /* ignore */ } } }, []);

    // ── Start: create the programVideos placeholder, then mount the recorder ──
    const handleStart = async () => {
        const t = title.trim();
        if (!t) { setErrorMsg('Please enter a title first.'); return; }
        setErrorMsg('');
        const id = await createProgramVideo({ programId, title: t, order: nextOrder, status: 'processing' });
        if (!id) {
            setErrorMsg('Could not start the recording. Check that Firestore rules allow the "programVideos" collection (see console).');
            return;
        }
        setMarker(id);
        setRecordingKey(id);
        setPhase('recording');
    };

    // ── Upload orchestration (program-scoped mirror of the class flow) ──
    const runUpload = useCallback(async (id, blob, mimeType) => {
        setPhase('uploading');
        setProgress(0);
        setErrorMsg('');
        try {
            const token = getToken ? await getToken() : null;
            const res = await fetch('/api/mux-direct-upload', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                body: JSON.stringify({ programId, programVideoId: id, classTitle: title.trim() }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.uploadUrl) throw new Error(data.error || 'Could not create the upload.');

            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhrRef.current = xhr;
                xhr.open('PUT', data.uploadUrl, true);
                xhr.setRequestHeader('Content-Type', mimeType || 'video/webm');
                xhr.upload.onprogress = (ev) => { if (ev.lengthComputable) setProgress(ev.loaded / ev.total); };
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) resolve();
                    else reject(new Error(`Upload rejected (HTTP ${xhr.status}).`));
                };
                xhr.onerror = () => reject(new Error('Upload failed — check your connection.'));
                xhr.onabort = () => reject(new Error('Upload cancelled.'));
                xhr.send(blob);
            });

            // PUT succeeded → wait for the webhook to flip the doc to 'ready'
            // before clearing the IDB buffer (conservative: never delete the
            // local recording until Mux confirms the asset, mirroring the class flow).
            setPhase('processing');
            if (unsubRef.current) { try { unsubRef.current(); } catch (_) { /* ignore */ } }
            unsubRef.current = onSnapshot(doc(db, 'programVideos', id), (snap) => {
                const d = snap.data();
                if (d && d.status === 'ready') {
                    if (unsubRef.current) { try { unsubRef.current(); } catch (_) { /* ignore */ } unsubRef.current = null; }
                    bufferClearRecording(id).catch(() => { /* non-critical */ });
                    clearMarker(id);
                    setPhase('done');
                    if (typeof onSaved === 'function') { try { onSaved(id); } catch (_) { /* defensive */ } }
                }
            });
        } catch (e) {
            setErrorMsg(e.message || 'Something went wrong.');
            setPhase('error');
        }
    }, [getToken, programId, title, onSaved]);

    const handleRecordingComplete = useCallback((blob, mimeType) => {
        if (!blob || blob.size === 0) { setErrorMsg('No video was captured.'); setPhase('error'); return; }
        blobRef.current = blob;
        mimeRef.current = mimeType || 'video/webm';
        if (recordingKey) runUpload(recordingKey, blob, mimeType);
    }, [recordingKey, runUpload]);

    const retry = () => { if (recordingKey && blobRef.current) runUpload(recordingKey, blobRef.current, mimeRef.current); };

    // ── Cancel / cleanup ──
    // If we abandon before the asset is finalized, delete the placeholder doc +
    // IDB chunks + marker so we don't leave an orphan 'processing' video.
    const cancel = async () => {
        if (xhrRef.current) { try { xhrRef.current.abort(); } catch (_) { /* ignore */ } }
        if (unsubRef.current) { try { unsubRef.current(); } catch (_) { /* ignore */ } unsubRef.current = null; }
        const id = recordingKey;
        if (id && phase !== 'done') {
            try { await bufferClearRecording(id); } catch (_) { /* ignore */ }
            try { await deleteProgramVideo(id); } catch (_) { /* ignore */ }
            clearMarker(id);
        }
        if (typeof onClose === 'function') onClose();
    };

    const overlay = { position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(20,16,14,0.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
    const card = { background: 'var(--mom-cream, #faf6f1)', borderRadius: 22, width: '100%', maxWidth: 860, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', padding: 24 };

    return (
        <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget && (phase === 'setup' || phase === 'error' || phase === 'done')) cancel(); }}>
            <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div>
                        <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 22, color: 'var(--mom-charcoal)' }}>Record a program video</h2>
                        {programTitle && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)', marginTop: 2 }}>for {programTitle}</p>}
                    </div>
                    <button onClick={cancel} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: 'var(--mom-warm-gray)', lineHeight: 1 }}>×</button>
                </div>

                {phase === 'setup' && (
                    <div>
                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-warm-gray)', display: 'block', marginBottom: 6 }}>Video title</label>
                        <input type="text" className="mom-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Day 1 — Full Body Warmup" style={{ marginBottom: 16 }} autoFocus />
                        {errorMsg && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-danger, #e53e3e)', marginBottom: 12 }}>{errorMsg}</p>}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button className="mom-btn mom-btn-primary" onClick={handleStart} style={{ padding: '11px 26px', fontSize: 14 }}>Continue to camera</button>
                            <button className="mom-btn mom-btn-ghost" onClick={cancel} style={{ fontSize: 13, color: 'var(--mom-warm-gray)' }}>Cancel</button>
                        </div>
                    </div>
                )}

                {phase === 'recording' && recordingKey && (
                    <LiveBroadcaster
                        classType="prerecorded"
                        classId={recordingKey}
                        endingAt={endingAt}
                        onEndClassRequest={() => setEndingAt(Date.now())}
                        onRecordingComplete={(blob, mimeType) => handleRecordingComplete(blob, mimeType)}
                    />
                )}

                {(phase === 'uploading' || phase === 'processing') && (
                    <div style={{ textAlign: 'center', padding: '24px 12px' }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 6 }}>
                            {phase === 'uploading' ? 'Uploading your recording' : 'Processing'}
                        </p>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)' }}>
                            {phase === 'uploading'
                                ? `${Math.round(progress * 100)}% — keep this tab open`
                                : 'Mux is finalizing your video. This usually takes 30s–2min. You can keep this open or close it — it will keep processing.'}
                        </p>
                        {phase === 'uploading' && (
                            <div style={{ height: 6, borderRadius: 4, background: 'var(--mom-cream-dark)', overflow: 'hidden', margin: '14px auto 0', maxWidth: 320 }}>
                                <div style={{ width: `${Math.max(2, Math.round(progress * 100))}%`, height: '100%', background: 'var(--mom-primary)', transition: 'width 0.2s' }} />
                            </div>
                        )}
                        {phase === 'processing' && (
                            <button className="mom-btn mom-btn-outline" onClick={onClose} style={{ marginTop: 18, fontSize: 13, padding: '9px 22px' }}>Close (keeps processing)</button>
                        )}
                    </div>
                )}

                {phase === 'error' && (
                    <div style={{ textAlign: 'center', padding: '24px 12px' }}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600, color: 'var(--mom-danger, #e53e3e)', marginBottom: 8 }}>Upload failed</p>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)', marginBottom: 18 }}>{errorMsg}</p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                            {blobRef.current && <button className="mom-btn mom-btn-primary" onClick={retry} style={{ padding: '10px 24px', fontSize: 14 }}>Retry upload</button>}
                            <button className="mom-btn mom-btn-ghost" onClick={cancel} style={{ fontSize: 13, color: 'var(--mom-warm-gray)' }}>Discard</button>
                        </div>
                    </div>
                )}

                {phase === 'done' && (
                    <div style={{ textAlign: 'center', padding: '24px 12px' }}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600, color: 'var(--mom-primary)', marginBottom: 8 }}>Video added ✓</p>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)', marginBottom: 18 }}>Your recording is ready and now part of this program.</p>
                        <button className="mom-btn mom-btn-primary" onClick={onClose} style={{ padding: '10px 24px', fontSize: 14 }}>Done</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProgramRecorder;
